// Castintech dashboard — Müşteri Memnuniyeti (NPS/CSAT/CES) view modülü.
// Aşama G G5 (2026-05-29, BD-2026-05-28-002) — EFQM K6 + Delivery SOP Katman 8.
//
// İçerik:
//   - 3 agregat kart (NPS / CSAT / CES) + response rate
//   - Survey tablosu (client, tip, milestone, status, score)
//   - efqm-gm CQO owner; /satisfaction + /satisfaction/aggregate endpoint

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, $ } from '../utils.js';
import { createSkeleton } from '../ui/state-display.js';

interface Survey {
  id: string;
  client_slug: string;
  project_slug: string | null;
  survey_type: 'csat' | 'nps' | 'ces';
  milestone: string;
  channel: string;
  status: string;
  score: number | null;
  comment: string | null;
  dispatched_at: string;
  responded_at: string | null;
}

interface Aggregate {
  nps: { score: number | null; promoters: number; detractors: number; total: number };
  csat: { avg: number | null; satisfied_pct: number | null; total: number };
  ces: { avg: number | null; total: number };
  response_rate_pct: number | null;
}

let _surveys: Survey[] = [];
let _agg: Aggregate | null = null;
let _error: string | null = null;
let _loaded = false;

export async function loadSatisfaction(): Promise<void> {
  if (!_loaded && state.currentPage === 'satisfaction') {
    const root = $('satisfaction-content');
    if (root && !root.querySelector('.sat-layout')) {
      root.innerHTML = '';
      root.appendChild(createSkeleton({ rows: 4, height: '70px', rowGap: '12px' }));
    }
  }
  try {
    const [aggRes, listRes] = await Promise.all([
      apiFetch('/castintech-api/satisfaction/aggregate'),
      apiFetch('/castintech-api/satisfaction'),
    ]);
    if (!aggRes.ok) throw new Error('aggregate HTTP ' + aggRes.status);
    if (!listRes.ok) throw new Error('list HTTP ' + listRes.status);
    _agg = await aggRes.json() as Aggregate;
    const d = await listRes.json() as { surveys: Survey[] };
    _surveys = d.surveys || [];
    _error = null;
  } catch (e) {
    _error = (e as Error).message || 'Memnuniyet verisi yüklenemedi';
    console.warn('loadSatisfaction failed:', _error);
  } finally {
    _loaded = true;
  }
  if (state.currentPage === 'satisfaction') renderSatisfaction();
}

function npsColor(s: number | null): string {
  if (s === null) return 'var(--muted)';
  if (s >= 50) return '#22c55e';
  if (s >= 0) return '#f59e0b';
  return '#ef4444';
}

const TYPE_LABEL: Record<string, string> = { csat: 'CSAT', nps: 'NPS', ces: 'CES' };
const STATUS_LABEL: Record<string, string> = {
  pending_dispatch: '⏳ Gönderim bekliyor', dispatched: '📤 Gönderildi',
  responded: '✅ Cevaplandı', expired: '⌛ Süresi doldu',
};

export function renderSatisfaction(): void {
  const root = $('satisfaction-content');
  if (!root) return;
  const sub = $('satisfaction-sub');
  if (sub) sub.textContent = 'EFQM K6 · NPS + CSAT + CES · efqm-gm (CQO)';

  if (_error) {
    root.innerHTML = `<div class="ds-error" role="alert" style="margin:8px 0">
      <div class="ds-error-icon" aria-hidden="true">⚠️</div>
      <div class="ds-error-title">Memnuniyet verisi yüklenemedi</div>
      <div class="ds-error-desc">${escHtml(_error)}</div>
      <button class="ds-error-retry" type="button" id="sat-retry-btn">Tekrar dene</button>
    </div>`;
    const btn = root.querySelector<HTMLButtonElement>('#sat-retry-btn');
    if (btn) btn.addEventListener('click', () => { void loadSatisfaction(); });
    return;
  }

  const a = _agg;
  const aggHtml = `
    <div class="sat-agg-grid">
      <div class="sat-agg-card" style="border-top:3px solid ${npsColor(a?.nps.score ?? null)}">
        <div class="sat-agg-label">NPS (Genel)</div>
        <div class="sat-agg-value" style="color:${npsColor(a?.nps.score ?? null)}">${a?.nps.score ?? '—'}</div>
        <div class="sat-agg-sub">${a ? `${a.nps.promoters} promoter · ${a.nps.detractors} detractor · ${a.nps.total} cevap` : ''}</div>
      </div>
      <div class="sat-agg-card" style="border-top:3px solid #22c55e">
        <div class="sat-agg-label">CSAT (T+7g)</div>
        <div class="sat-agg-value" style="color:#22c55e">${a?.csat.avg ?? '—'}<span class="sat-agg-unit">/5</span></div>
        <div class="sat-agg-sub">${a && a.csat.satisfied_pct !== null ? `%${a.csat.satisfied_pct} memnun · ${a.csat.total} cevap` : 'veri yok'}</div>
      </div>
      <div class="sat-agg-card" style="border-top:3px solid #818cf8">
        <div class="sat-agg-label">CES (T+30g)</div>
        <div class="sat-agg-value" style="color:#818cf8">${a?.ces.avg ?? '—'}<span class="sat-agg-unit">/7</span></div>
        <div class="sat-agg-sub">${a ? `${a.ces.total} cevap · düşük = iyi` : ''}</div>
      </div>
      <div class="sat-agg-card" style="border-top:3px solid #94a3b8">
        <div class="sat-agg-label">Yanıt Oranı</div>
        <div class="sat-agg-value" style="color:#94a3b8">${a?.response_rate_pct ?? '—'}<span class="sat-agg-unit">%</span></div>
        <div class="sat-agg-sub">cevaplanan / ulaşılan</div>
      </div>
    </div>`;

  let tableHtml: string;
  if (_surveys.length === 0) {
    tableHtml = `<div class="ds-empty" role="status" style="margin:8px 0">
      <div class="ds-empty-icon" aria-hidden="true">📭</div>
      <div class="ds-empty-title">Henüz anket yok</div>
      <div class="ds-empty-desc">efqm-gm T+7g/T+30g/T+90g milestone'larında otomatik anket açar (08:30 cron).</div>
    </div>`;
  } else {
    tableHtml = `<table class="sat-table">
      <thead><tr><th>Müşteri</th><th>Tip</th><th>Milestone</th><th>Durum</th><th>Skor</th><th>Tarih</th></tr></thead>
      <tbody>${_surveys.map(s => `
        <tr>
          <td>${escHtml(s.client_slug)}${s.project_slug ? ` <span class="sat-proj">/${escHtml(s.project_slug)}</span>` : ''}</td>
          <td><span class="sat-type sat-type--${s.survey_type}">${TYPE_LABEL[s.survey_type] || s.survey_type}</span></td>
          <td>${escHtml(s.milestone)}</td>
          <td>${STATUS_LABEL[s.status] || escHtml(s.status)}</td>
          <td>${s.score !== null ? `<strong>${s.score}</strong>` : '—'}</td>
          <td class="sat-date">${escHtml((s.responded_at || s.dispatched_at || '').slice(0, 10))}</td>
        </tr>`).join('')}</tbody>
    </table>`;
  }

  root.innerHTML = `
    <div class="sat-layout">
      ${aggHtml}
      <div class="sat-section">
        <div class="sat-section-title">📋 Anket Kayıtları (${_surveys.length})</div>
        ${tableHtml}
        <div style="margin-top:8px;font-size:11px;color:var(--muted)">
          NPS: promoter(9-10) − detractor(0-6) · CSAT: 1-5 ort · CES: 1-7 ort (düşük=iyi) · CHARTER § 1.5 K6
        </div>
      </div>
    </div>`;
}
